import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/api';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';

interface VendorConfiguration {
  id: number;
  enableTableManagement: boolean;
  enableFloorManagement: boolean;
  enableSectionManagement: boolean;
  enableMultipleMenus: boolean;
  enableKitchenDisplay: boolean;
  enableOrderLines: boolean;
  enableStewardFeatures: boolean;
  enableWhatsappOrderConfirmation: boolean;
  enableWhatsappOrdering: boolean;
  whatsappPhoneNumberId: string;
  whatsappBusinessNumber: string;
  allowGuestTableOrders: boolean;
  enableLoyaltyPrograms: boolean;
  businessType: string;
  simpleMode: boolean;
  defaultOrderType: string;
  autoAcceptOrders: boolean;
  /** True: a live offer comes off the bill on its own. False: staff confirm first. */
  autoApplyOffers: boolean;
  orderPreparationTime: number;
  // Print settings
  autoPrintKotOnOrder: boolean;
  autoPrintInvoiceOnSettle: boolean;
  // Inventory settings
  enableInventoryManagement: boolean;
  /** true: POS orders drive sales history + stock deduction; false: standalone manual daily sales */
  inventoryPosIntegrated: boolean;
  inventoryDeductOnManualSales: boolean;
  // Assistant / integrations
  enableAiAssistant?: boolean;
  enableTelegramBot?: boolean;
}

interface VendorBranding {
  businessName?: string;
  logoUrl?: string;
}

interface VendorFeatures {
  simpleMode: boolean;
  tableManagement: boolean;
  floorManagement: boolean;
  sectionManagement: boolean;
  multipleMenus: boolean;
  kitchenDisplay: boolean;
  orderLines: boolean;
  stewardFeatures: boolean;
  whatsappOrderConfirmation: boolean;
  loyaltyPrograms: boolean;
  /** Tips distribution (UK plan Phase 4) — surfaced for GB vendors only */
  tipsDistribution: boolean;
  inventoryManagement: boolean;
  reservations?: boolean;
  aiAssistant?: boolean;
}

interface VendorConfigContextType {
  configuration: VendorConfiguration | null;
  features: VendorFeatures | null;
  branding: VendorBranding | null;
  loading: boolean;
  error: string | null;
  updateConfiguration: (updates: Partial<VendorConfiguration>) => Promise<void>;
  enableSimpleMode: () => Promise<void>;
  disableSimpleMode: () => Promise<void>;
  refreshConfiguration: () => Promise<void>;
}

const VendorConfigContext = createContext<VendorConfigContextType | undefined>(undefined);

const useVendorConfig = () => {
  const context = useContext(VendorConfigContext);
  if (context === undefined) {
    throw new Error('useVendorConfig must be used within a VendorConfigProvider');
  }
  return context;
};

interface VendorConfigProviderProps {
  children: React.ReactNode;
}

const VendorConfigProvider: React.FC<VendorConfigProviderProps> = ({ children }) => {
  const { user, isVendor, isSteward } = useAuth();
  const { selectTheme } = useTheme();
  const [configuration, setConfiguration] = useState<VendorConfiguration | null>(null);
  const [features, setFeatures] = useState<VendorFeatures | null>(null);
  const [branding, setBranding] = useState<VendorBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfiguration = async () => {
    // For vendors: fetch full configuration and branding
    if (isVendor) {
      try {
        setLoading(true);
        setError(null);

        // Fetch configuration and branding info in parallel
        const [configResponse, taxConfigResponse] = await Promise.all([
          api.get('/api/vendor/configuration'),
          api.get('/api/vendor/tax-config').catch(() => ({ data: null })) // Don't fail if tax config doesn't exist
        ]);

        setConfiguration(configResponse.data.configuration);
        setFeatures(configResponse.data.features);

        // Extract branding info from tax config or use username as fallback
        if (taxConfigResponse.data) {
          setBranding({
            businessName: taxConfigResponse.data.businessName || user?.username,
            logoUrl: taxConfigResponse.data.logoUrl
          });
          // Apply vendor's theme preset
          if (taxConfigResponse.data.themePreset) {
            selectTheme(taxConfigResponse.data.themePreset);
          }
        } else {
          setBranding({
            businessName: user?.username
          });
        }
      } catch (err: unknown) {
        setError((err as Error & { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to load vendor configuration');
        console.error('Error fetching vendor configuration:', err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // For stewards: fetch only branding from their vendor
    if (isSteward) {
      try {
        setLoading(true);
        setError(null);

        const brandingResponse = await api.get('/api/steward/vendor-branding');

        if (brandingResponse.data && brandingResponse.data.logoUrl) {
          setBranding({
            businessName: brandingResponse.data.businessName,
            logoUrl: brandingResponse.data.logoUrl
          });
        } else {
          setBranding({
            businessName: brandingResponse.data?.businessName || user?.username
          });
        }
        // Apply vendor's theme preset for stewards
        if (brandingResponse.data?.themePreset) {
          selectTheme(brandingResponse.data.themePreset);
        }
      } catch (err: unknown) {
        console.error('Error fetching steward vendor branding:', err);
        // Don't set error for stewards - branding is optional
      } finally {
        setLoading(false);
      }
      return;
    }

    // Not a vendor or steward
    setLoading(false);
  };

  useEffect(() => {
    if (user && (isVendor || isSteward)) {
      fetchConfiguration();
    }
  }, [user, isVendor, isSteward]);

  const updateConfiguration = async (updates: Partial<VendorConfiguration>) => {
    try {
      setError(null);
      const response = await api.put('/api/vendor/configuration', updates);
      setConfiguration(response.data.configuration);
      setFeatures(response.data.features);
    } catch (err: unknown) {
      setError((err as Error & { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to update configuration');
      throw err;
    }
  };

  const enableSimpleMode = async () => {
    try {
      setError(null);
      const response = await api.post('/api/vendor/configuration/simple-mode/enable');
      setConfiguration(response.data.configuration);
      await fetchConfiguration(); // Refresh to get updated features
    } catch (err: unknown) {
      setError((err as Error & { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to enable simple mode');
      throw err;
    }
  };

  const disableSimpleMode = async () => {
    try {
      setError(null);
      const response = await api.post('/api/vendor/configuration/simple-mode/disable');
      setConfiguration(response.data.configuration);
      await fetchConfiguration(); // Refresh to get updated features
    } catch (err: unknown) {
      setError((err as Error & { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to disable simple mode');
      throw err;
    }
  };

  const refreshConfiguration = async () => {
    await fetchConfiguration();
  };

  const value: VendorConfigContextType = {
    configuration,
    features,
    branding,
    loading,
    error,
    updateConfiguration,
    enableSimpleMode,
    disableSimpleMode,
    refreshConfiguration,
  };

  return (
    <VendorConfigContext.Provider value={value}>
      {children}
    </VendorConfigContext.Provider>
  );
};

// Helper hook to check if a feature is enabled
const useFeature = (feature: keyof VendorFeatures): boolean => {
  const { features } = useVendorConfig();
  return features ? features[feature] : false;
};

// Export all hooks and components
export { VendorConfigProvider, useVendorConfig, useFeature };